import * as React from 'react';
import { HelpCircle, TrendingUp } from 'lucide-react';
import { Switch } from '../ui/Switch';
import { Tooltip } from '../ui/Tooltip';
import {
  EntrepreneurialSynergyAnswers,
  calculateEntrepreneurialMultiplier
} from '../../types/entrepreneurial-synergy';

interface EntrepreneurialSynergyBoxProps {
  value: EntrepreneurialSynergyAnswers;
  onChange: (next: EntrepreneurialSynergyAnswers) => void;
  disabled?: boolean;
  predictedValue?: number;
}

interface SynergyRowProps {
  id: string;
  label: string;
  tooltip: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  impact: string;
}

function SynergyRow({
  id,
  label,
  tooltip,
  checked,
  onCheckedChange,
  disabled,
  impact
}: SynergyRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <label htmlFor={id} className="text-sm text-gray-700 cursor-pointer flex-1">
          {label}
        </label>
        <Tooltip content={tooltip}>
          <HelpCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </Tooltip>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500 font-medium w-12 text-right">{impact}</span>
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function EntrepreneurialSynergyBox({
  value,
  onChange,
  disabled,
  predictedValue
}: EntrepreneurialSynergyBoxProps) {
  function set<K extends keyof EntrepreneurialSynergyAnswers>(key: K, next: boolean) {
    onChange({ ...value, [key]: next });
  }

  const { multiplier, uplift, factors } = calculateEntrepreneurialMultiplier(value);
  const entrepreneurialValue = predictedValue ? predictedValue * multiplier : 0;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Your Operating Advantages
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Optional - Estimate the additional value you could create through your operation
            </p>
          </div>
        </div>

        {/* Preview Card */}
        {predictedValue && predictedValue > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-800 font-medium">Estimated Entrepreneurial Value</p>
                <p className="text-xs text-green-600 mt-0.5">
                  Based on {factors.length > 0 ? `${factors.length} synergy factor${factors.length !== 1 ? 's' : ''}` : 'baseline assumptions'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-700">
                  ${(entrepreneurialValue / 1000000).toFixed(2)}M
                </p>
                <p className="text-sm text-green-600">
                  +{(uplift * 100).toFixed(1)}% uplift
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-1 border-t border-gray-100 pt-4">
          {/* Strategic Synergies Section */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Strategic Synergies
            </h4>

            <SynergyRow
              id="rebrandToExistingBrand"
              label="Rebrand to a brand you already operate"
              tooltip="Leveraging existing brand equity, signage, and customer recognition can increase property value through proven brand performance and reduced marketing costs."
              checked={value.rebrandToExistingBrand}
              onCheckedChange={(v) => set('rebrandToExistingBrand', v)}
              disabled={disabled}
              impact="+3%"
            />

            <SynergyRow
              id="integrateFuelSupply"
              label="Integrate fuel supply under your current contract"
              tooltip="Consolidating fuel purchases under existing wholesale agreements typically improves margins through volume discounts and favorable terms negotiated across your portfolio."
              checked={value.integrateFuelSupply}
              onCheckedChange={(v) => set('integrateFuelSupply', v)}
              disabled={disabled}
              impact="+3%"
            />

            <SynergyRow
              id="contributesToTerritory"
              label="Adds to your existing regional territory"
              tooltip="Geographic clustering creates operational efficiencies through shared logistics, regional marketing campaigns, and optimized distribution routes across your network."
              checked={value.contributesToTerritory}
              onCheckedChange={(v) => set('contributesToTerritory', v)}
              disabled={disabled}
              impact="+2%"
            />
          </div>

          {/* Operational Efficiencies Section */}
          <div className="mb-4 pt-2 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Operational Efficiencies
            </h4>

            <SynergyRow
              id="bringExistingMgmtOps"
              label="Bring your existing management systems"
              tooltip="Deploying proven operational procedures, training programs, and management talent reduces ramp-up time and minimizes execution risk during transition."
              checked={value.bringExistingMgmtOps}
              onCheckedChange={(v) => set('bringExistingMgmtOps', v)}
              disabled={disabled}
              impact="+2%"
            />

            <SynergyRow
              id="reduceCorporateOverhead"
              label="Reduce overhead via shared admin/accounting"
              tooltip="Centralizing back-office functions like accounting, HR, and compliance across multiple locations lowers per-site overhead and improves profitability."
              checked={value.reduceCorporateOverhead}
              onCheckedChange={(v) => set('reduceCorporateOverhead', v)}
              disabled={disabled}
              impact="+2%"
            />
          </div>

          {/* Value Enhancement Section */}
          <div className="mb-4 pt-2 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Value Enhancement
            </h4>

            <SynergyRow
              id="plannedCapexModernization"
              label="Planned capital improvements post-acquisition"
              tooltip="Strategic reinvestment in pumps, car wash equipment, store remodeling, or technology upgrades can significantly increase revenue potential and competitive positioning."
              checked={value.plannedCapexModernization}
              onCheckedChange={(v) => set('plannedCapexModernization', v)}
              disabled={disabled}
              impact="+3%"
            />
          </div>

          {/* Experience Factor Section */}
          <div className="pt-2 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Experience Factor
            </h4>

            <SynergyRow
              id="operatesOtherLocations"
              label="I currently operate other fuel/convenience locations"
              tooltip="Operating experience reduces execution risk and validates your ability to achieve projected performance. First-time operators may face a learning curve that impacts near-term value creation."
              checked={value.operatesOtherLocations}
              onCheckedChange={(v) => set('operatesOtherLocations', v)}
              disabled={disabled}
              impact={value.operatesOtherLocations ? '—' : '-3%'}
            />
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs leading-relaxed text-gray-500">
            <strong>Note:</strong> This estimates the <em>additional value you could create</em> through your specific operating advantages.
            It does not change the base property valuation. First-time operators can still achieve strong returns, though synergy gains may be more limited initially.
          </p>
        </div>

        {/* Breakdown (Optional Display) */}
        {factors.length > 0 && (
          <details className="mt-4 group">
            <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              <span>View detailed breakdown</span>
              <svg
                className="w-4 h-4 transform transition-transform group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs space-y-1">
              <div className="flex justify-between font-medium text-gray-700 border-b border-gray-200 pb-1">
                <span>Factor</span>
                <span>Impact</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Baseline</span>
                <span>+10.0%</span>
              </div>
              {factors.map((factor, idx) => (
                <div key={idx} className="flex justify-between text-gray-600">
                  <span>{factor.name}</span>
                  <span className={factor.impact < 0 ? 'text-red-600' : 'text-green-600'}>
                    {factor.impact >= 0 ? '+' : ''}{(factor.impact * 100).toFixed(1)}%
                  </span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1 mt-1">
                <span>Total Uplift</span>
                <span className="text-green-700">+{(uplift * 100).toFixed(1)}%</span>
              </div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
